import type { FluidPropertiesContext } from "../fluidProperties/d";
import {
  type Casing_Flow_Options_Enum,
  Tubing_Flow_Options_Enum
} from "gql/api";
import { getDunsRosFrictionProperties } from "./dunsRos";
import {
  getOrkiszewiskiFrictionProperties,
  OrkiszewskiFlowRegime
} from "./orkiszewiski";
import { getBeggsBrillFrictionProperties } from "./beggsBrill";
import { getPipeFrictionContext } from "./getPipeFrictionContext";
import { getHagedornBrownFrictionProperties } from "./hagedornBrown";
import { getHazenWilliamsFrictionLossPSIA } from "./getHazenWilliamsFrictionLoss";

// Both enums share the same string values — this union lets callers pass either.
export type PipeFlowMethod =
  | Tubing_Flow_Options_Enum
  | Casing_Flow_Options_Enum;

export type PipeFrictionIntermediates = {
  // THESE ARE ALWAYS CALCULATED AND USED TO CALC TOTAL FRICTION LOSS AT TOP LEVEL
  frictionPressurePSIA: number;
  // BELOW HERE IS VARIABLE DEPENDING ON THE METHOD
  [key: string]: unknown;
};
export type PipeFrictionProperties = {
  calcMethod: PipeFlowMethod;
  frictionPressurePSIA: number;
  hydrostaticPressurePSIA: number;
  intermediates: PipeFrictionIntermediates;
};
export type AggregatePipeFrictionProperties = {
  frictionPressurePSIA: number;
  requiredDischargePressurePSIA: number;
  topPipeFrictionProperties: PipeFrictionProperties;
  bottomPipeFrictionProperties: PipeFrictionProperties;
};

type HazenWilliamsAction = "none" | "add" | "replace";

export const computePipeFrictionProperties = (
  phiRad: number,
  surfaceTemperatureF: number,
  bottomholeTemperatureF: number,
  pipeLengthFt: number,
  pipeInteriorDiameterIn: number,
  pipeDepthFt: number,
  fluidPropertiesContext: FluidPropertiesContext,
  pipeFlow: PipeFlowMethod,
  applyHazenWilliams: boolean,
  hazenWilliamsCoefficient: number,
  pipeRoughnessIn: number,
  palmerCorrection: boolean,
  griffithWallis: boolean,
  backpressurePSIA: number,
  referencePointPressurePSIA: number,
  annulusOuterDiameterIn?: number,
  annulusInnerDiameterIn?: number,
  annulusInnerWallRoughnessIn?: number
): PipeFrictionProperties => {
  const useAnnulusDiameter =
    annulusOuterDiameterIn !== undefined &&
    annulusInnerDiameterIn !== undefined &&
    annulusOuterDiameterIn > annulusInnerDiameterIn;

  const effectivePipeDiameterIn = useAnnulusDiameter
    ? annulusOuterDiameterIn - annulusInnerDiameterIn
    : pipeInteriorDiameterIn;

  const computeSingleFrictionPass = (wallRoughnessIn: number) => {
    const context = getPipeFrictionContext(
      phiRad,
      surfaceTemperatureF,
      bottomholeTemperatureF,
      pipeLengthFt,
      effectivePipeDiameterIn,
      pipeDepthFt,
      fluidPropertiesContext,
      wallRoughnessIn,
      referencePointPressurePSIA,
      palmerCorrection,
      backpressurePSIA,
      griffithWallis
    );

    const {
      fluidPropertiesContext: {
        flowRatesInSitu,
        fluidProperties: { fluidSpecificGravity },
        fluidFrictionProperties: { flowRegime }
      }
    } = context;

    const liquidFlowRateBBLD = flowRatesInSitu?.liquidFlowRateBBLD ?? 0;

    const hazenWilliamsFrictionPressurePSIA = getHazenWilliamsFrictionLossPSIA(
      effectivePipeDiameterIn,
      liquidFlowRateBBLD,
      hazenWilliamsCoefficient || 0,
      pipeLengthFt,
      fluidSpecificGravity
    );

    const hasValidHazenWilliamsCoefficient =
      Number.isFinite(hazenWilliamsCoefficient) && hazenWilliamsCoefficient > 0;
    let hazenWilliamsAction: HazenWilliamsAction =
      applyHazenWilliams && hasValidHazenWilliamsCoefficient
        ? "replace"
        : "none";
    let intermediates;
    if (pipeFlow === Tubing_Flow_Options_Enum.HagedornBrown) {
      intermediates = getHagedornBrownFrictionProperties(context);
    } else if (pipeFlow === Tubing_Flow_Options_Enum.DunsRos) {
      intermediates = getDunsRosFrictionProperties(context);
    } else if (pipeFlow === Tubing_Flow_Options_Enum.Orkiszewski) {
      if (
        flowRegime === OrkiszewskiFlowRegime.TRANSITION &&
        hazenWilliamsAction === "replace"
      ) {
        hazenWilliamsAction = "add";
      }
      intermediates = getOrkiszewiskiFrictionProperties(context);
    } else if (pipeFlow === Tubing_Flow_Options_Enum.BeggsBrill) {
      intermediates = getBeggsBrillFrictionProperties(context);
    } else {
      throw new Error(`Invalid Tubing Flow Option: ${pipeFlow}`);
    }

    const { frictionPressurePSIA: tempFrictionPressurePSIA } = intermediates;
    let frictionPressurePSIA = tempFrictionPressurePSIA;
    if (hazenWilliamsAction === "add") {
      frictionPressurePSIA =
        frictionPressurePSIA + hazenWilliamsFrictionPressurePSIA;
    } else if (hazenWilliamsAction === "replace") {
      frictionPressurePSIA = hazenWilliamsFrictionPressurePSIA;
    }

    const {
      fluidPropertiesContext: junk1, // eslint-disable-line @typescript-eslint/no-unused-vars
      ...derivedContext
    } = context;

    return {
      frictionPressurePSIA,
      hazenWilliamsAction,
      intermediates,
      derivedContext
    };
  };

  if (useAnnulusDiameter) {
    const outerWallPass = computeSingleFrictionPass(pipeRoughnessIn);
    const innerWallPass = computeSingleFrictionPass(
      annulusInnerWallRoughnessIn ?? pipeRoughnessIn
    );
    const annulusFrictionPressurePSIA =
      outerWallPass.frictionPressurePSIA + innerWallPass.frictionPressurePSIA;

    return {
      calcMethod: pipeFlow,
      frictionPressurePSIA: annulusFrictionPressurePSIA,
      hydrostaticPressurePSIA: 0,
      intermediates: {
        ...outerWallPass.intermediates,
        frictionPressurePSIA: annulusFrictionPressurePSIA,
        hazenWilliamsAction: outerWallPass.hazenWilliamsAction,
        context: outerWallPass.derivedContext,
        annulusOuterWall: {
          ...outerWallPass.intermediates,
          frictionPressurePSIA: outerWallPass.frictionPressurePSIA,
          hazenWilliamsAction: outerWallPass.hazenWilliamsAction,
          context: outerWallPass.derivedContext
        },
        annulusInnerWall: {
          ...innerWallPass.intermediates,
          frictionPressurePSIA: innerWallPass.frictionPressurePSIA,
          hazenWilliamsAction: innerWallPass.hazenWilliamsAction,
          context: innerWallPass.derivedContext
        }
      }
    };
  }

  const singlePass = computeSingleFrictionPass(pipeRoughnessIn);

  return {
    calcMethod: pipeFlow,
    frictionPressurePSIA: singlePass.frictionPressurePSIA,
    hydrostaticPressurePSIA: 0,
    intermediates: {
      ...singlePass.intermediates,
      hazenWilliamsAction: singlePass.hazenWilliamsAction,
      context: singlePass.derivedContext
    }
  };
};

export const computeRequiredDischargePressure = (
  pipeFlow: PipeFlowMethod,
  flowRegime: string,
  backpressurePSIA: number,
  hydrostaticSum: number,
  frictionPressurePSIA: number,
  frictionProperties: PipeFrictionProperties
): number => {
  let hydrostaticPressurePSIA = 0;
  if (pipeFlow === Tubing_Flow_Options_Enum.HagedornBrown) {
    const mixtureDensityLBFT3 = frictionProperties.intermediates
      .mixtureDensityLBFT3 as number;
    hydrostaticPressurePSIA = (mixtureDensityLBFT3 / 144) * hydrostaticSum;
  } else if (pipeFlow === Tubing_Flow_Options_Enum.DunsRos) {
    const staticGradient = frictionProperties.intermediates
      .staticGradient as number;
    hydrostaticPressurePSIA = staticGradient * hydrostaticSum;
  } else if (pipeFlow === Tubing_Flow_Options_Enum.Orkiszewski) {
    if (flowRegime === OrkiszewskiFlowRegime.LIQUID) {
      const mixtureDensityPM = frictionProperties.intermediates
        .mixtureDensityPM as number;
      hydrostaticPressurePSIA = (mixtureDensityPM / 144) * hydrostaticSum;
    } else if (flowRegime === OrkiszewskiFlowRegime.BUBBLE) {
      const mixtureDensityPM = frictionProperties.intermediates
        .mixtureDensityPM as number;
      hydrostaticPressurePSIA = (mixtureDensityPM / 144) * hydrostaticSum;
    } else if (flowRegime === OrkiszewskiFlowRegime.SLUG) {
      const mixtureDensityPM = frictionProperties.intermediates
        .mixtureDensityPM as number;
      hydrostaticPressurePSIA = (mixtureDensityPM / 144) * hydrostaticSum;
    } else if (flowRegime === OrkiszewskiFlowRegime.MIST) {
      const staticGradient = frictionProperties.intermediates
        .staticGradient as number;
      hydrostaticPressurePSIA = staticGradient * hydrostaticSum;
    } else if (flowRegime === OrkiszewskiFlowRegime.TRANSITION) {
      const totalGradient = frictionProperties.intermediates
        .totalGradient as number;
      hydrostaticPressurePSIA = totalGradient * hydrostaticSum;
    } else {
      hydrostaticPressurePSIA = 0;
    }
  } else {
    // Only other option is beggsbrill
    const beggsFluidSpecificGravity = frictionProperties.intermediates
      .beggsFluidSpecificGravity as number;
    hydrostaticPressurePSIA =
      ((beggsFluidSpecificGravity * 62.428) / 144) * hydrostaticSum;
  }
  return hydrostaticPressurePSIA + frictionPressurePSIA + backpressurePSIA;
};

export const computePipeHydrostaticPressure = (
  pipeFlow: PipeFlowMethod,
  flowRegime: string,
  hydrostaticSum: number,
  frictionProperties: PipeFrictionProperties
): number => {
  let hydrostaticPressurePSIA = 0;
  if (pipeFlow === Tubing_Flow_Options_Enum.HagedornBrown) {
    const mixtureDensityLBFT3 = frictionProperties.intermediates
      .mixtureDensityLBFT3 as number;
    hydrostaticPressurePSIA = (mixtureDensityLBFT3 / 144) * hydrostaticSum;
  } else if (pipeFlow === Tubing_Flow_Options_Enum.DunsRos) {
    const staticGradient = frictionProperties.intermediates
      .staticGradient as number;
    hydrostaticPressurePSIA = staticGradient * hydrostaticSum;
  } else if (pipeFlow === Tubing_Flow_Options_Enum.Orkiszewski) {
    if (flowRegime === OrkiszewskiFlowRegime.LIQUID) {
      const mixtureDensityPM = frictionProperties.intermediates
        .mixtureDensityPM as number;
      hydrostaticPressurePSIA = (mixtureDensityPM / 144) * hydrostaticSum;
    } else if (flowRegime === OrkiszewskiFlowRegime.BUBBLE) {
      const mixtureDensityPM = frictionProperties.intermediates
        .mixtureDensityPM as number;
      hydrostaticPressurePSIA = (mixtureDensityPM / 144) * hydrostaticSum;
    } else if (flowRegime === OrkiszewskiFlowRegime.SLUG) {
      const mixtureDensityPM = frictionProperties.intermediates
        .mixtureDensityPM as number;
      hydrostaticPressurePSIA = (mixtureDensityPM / 144) * hydrostaticSum;
    } else if (flowRegime === OrkiszewskiFlowRegime.MIST) {
      const staticGradient = frictionProperties.intermediates
        .staticGradient as number;
      hydrostaticPressurePSIA = staticGradient * hydrostaticSum;
    } else if (flowRegime === OrkiszewskiFlowRegime.TRANSITION) {
      const totalGradient = frictionProperties.intermediates
        .totalGradient as number;
      hydrostaticPressurePSIA = totalGradient * hydrostaticSum;
    } else {
      hydrostaticPressurePSIA = 0;
    }
  } else {
    // Only other option is beggsbrill
    const beggsFluidSpecificGravity = frictionProperties.intermediates
      .beggsFluidSpecificGravity as number;
    hydrostaticPressurePSIA =
      ((beggsFluidSpecificGravity * 62.428) / 144) * hydrostaticSum;
  }
  return hydrostaticPressurePSIA;
};
